import { useRef, useState } from 'react';
import { Star, MoreVertical, Folder as FolderIcon } from 'lucide-react';
import { Badge, Card } from '@/design-system';
import { navigate } from '@/app/router';
import type { FunnelSummary } from '@/contracts/funnel';

export interface FunnelCardProps {
  funnel: FunnelSummary;
  onToggleFavorite: (funnel: FunnelSummary) => void;
  onRename: (funnel: FunnelSummary) => void;
  onDuplicate: (funnel: FunnelSummary) => void;
  onArchiveToggle: (funnel: FunnelSummary) => void;
  onDelete: (funnel: FunnelSummary) => void;
  onMoveToFolder: (funnel: FunnelSummary) => void;
}

/**
 * SPEC-003 §40/T043-T045: card shows name/updated/folder/tags/favorite and
 * a context menu with Open/Rename/Duplicate/Favorite/Archive/Delete. No
 * real thumbnail (§41) — a placeholder graphic stands in for the canvas.
 */
export function FunnelCard({
  funnel,
  onToggleFavorite,
  onRename,
  onDuplicate,
  onArchiveToggle,
  onDelete,
  onMoveToFolder,
}: FunnelCardProps) {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [favoritePending, setFavoritePending] = useState(false);

  function closeMenu() {
    if (detailsRef.current) detailsRef.current.open = false;
  }

  function openFunnel() {
    navigate(`/app/funnels/${funnel.id}`);
  }

  async function handleFavoriteClick(event: React.MouseEvent) {
    event.stopPropagation();
    setFavoritePending(true);
    try {
      await onToggleFavorite(funnel);
    } finally {
      setFavoritePending(false);
    }
  }

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={openFunnel}
      onKeyDown={(e) => {
        if (e.key === 'Enter') openFunnel();
      }}
      className="group flex cursor-pointer flex-col gap-3 transition-colors hover:border-border-brand"
    >
      <div className="flex h-20 items-center justify-center rounded-md border border-border-subtle bg-surface-2 text-text-muted">
        <svg width="40" height="28" viewBox="0 0 40 28" aria-hidden="true">
          <rect x="1" y="4" width="10" height="8" rx="2" fill="currentColor" opacity="0.5" />
          <rect x="1" y="16" width="10" height="8" rx="2" fill="currentColor" opacity="0.5" />
          <rect x="24" y="10" width="15" height="8" rx="2" fill="currentColor" opacity="0.8" />
          <path d="M11 8H17V14H24" stroke="currentColor" fill="none" strokeWidth="1.5" opacity="0.5" />
          <path d="M11 20H17V14" stroke="currentColor" fill="none" strokeWidth="1.5" opacity="0.5" />
        </svg>
      </div>

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-text-primary">{funnel.name}</p>
          {funnel.description ? (
            <p className="truncate text-xs text-text-muted">{funnel.description}</p>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            aria-label={funnel.isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
            aria-pressed={funnel.isFavorite}
            disabled={favoritePending}
            onClick={handleFavoriteClick}
            className="rounded p-1 text-text-muted hover:text-accent disabled:opacity-50"
          >
            <Star size={16} fill={funnel.isFavorite ? 'currentColor' : 'none'} className={funnel.isFavorite ? 'text-accent' : ''} />
          </button>

          <details ref={detailsRef} className="relative" onClick={(e) => e.stopPropagation()}>
            <summary
              aria-label="Ações do funil"
              className="flex h-6 w-6 cursor-pointer list-none items-center justify-center rounded text-text-muted hover:text-text-secondary"
            >
              <MoreVertical size={16} />
            </summary>
            <div className="absolute right-0 z-10 mt-1 w-40 rounded-md border border-border bg-surface-2 p-1 shadow-md">
              <button
                type="button"
                onClick={() => {
                  closeMenu();
                  openFunnel();
                }}
                className="block w-full rounded-sm px-3 py-1.5 text-left text-sm text-text-primary hover:bg-surface"
              >
                Abrir
              </button>
              <button
                type="button"
                onClick={() => {
                  closeMenu();
                  onRename(funnel);
                }}
                className="block w-full rounded-sm px-3 py-1.5 text-left text-sm text-text-primary hover:bg-surface"
              >
                Renomear
              </button>
              <button
                type="button"
                onClick={() => {
                  closeMenu();
                  onDuplicate(funnel);
                }}
                className="block w-full rounded-sm px-3 py-1.5 text-left text-sm text-text-primary hover:bg-surface"
              >
                Duplicar
              </button>
              <button
                type="button"
                onClick={() => {
                  closeMenu();
                  onMoveToFolder(funnel);
                }}
                className="block w-full rounded-sm px-3 py-1.5 text-left text-sm text-text-primary hover:bg-surface"
              >
                Mover para pasta
              </button>
              <button
                type="button"
                onClick={() => {
                  closeMenu();
                  onArchiveToggle(funnel);
                }}
                className="block w-full rounded-sm px-3 py-1.5 text-left text-sm text-text-primary hover:bg-surface"
              >
                {funnel.status === 'archived' ? 'Desarquivar' : 'Arquivar'}
              </button>
              <button
                type="button"
                onClick={() => {
                  closeMenu();
                  onDelete(funnel);
                }}
                className="block w-full rounded-sm px-3 py-1.5 text-left text-sm text-[#f08484] hover:bg-surface"
              >
                Excluir
              </button>
            </div>
          </details>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 text-xs text-text-muted">
        {funnel.folder ? (
          <span className="inline-flex items-center gap-1">
            <FolderIcon size={12} /> {funnel.folder.name}
          </span>
        ) : null}
        {funnel.status === 'archived' ? <Badge>Arquivado</Badge> : null}
        {funnel.tags.slice(0, 3).map((tag) => (
          <Badge key={tag.id}>{tag.name}</Badge>
        ))}
      </div>

      <p className="text-[11px] text-text-muted">
        Atualizado em {new Date(funnel.updatedAt).toLocaleDateString('pt-BR')}
      </p>
    </Card>
  );
}
